import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { trackMiniAppEvent } from '../../utils/analytics';
import {
  instructionsStyle,
  primaryBtn,
  secondaryBtn,
  tapRoundNeutralChoiceBtn,
  feedbackCorrect,
  feedbackWarmHint,
  engineShellGuided,
} from './shared/engineTheme';
import { useCelebration, CelebrationOverlay, A11yAnnouncement } from './shared/Celebration';
import { useGuidedHints } from './shared/useGuidedHints';
import GuidedHintOverlay, {
  GuidedHintHelpButton,
  guidedHintReadAloud,
} from './shared/GuidedHintOverlay';
import { isTapFirstLessonUi } from './shared/lessonUiMode';
import { shouldSuppressActivityTitle } from './shared/guidedPromptUtils';

const SEQUENCE_TAP_MECHANIC_HINT = {
  text: 'Tap the steps in the order they should happen. Tap a selected step to undo from there.',
  audioText:
    'Tap the steps in the order they should happen. Tap a selected step to undo from there.',
};

function shuffleIds(items) {
  if (!items?.length) return [];
  const copy = items.map((i) => i.id);
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

/** Flat shell inside lesson card — uses shared guided token */
const missionShell = {
  ...engineShellGuided,
  padding: '2px 0 8px',
};

const badgeStyle = {
  flexShrink: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 26,
  height: 26,
  padding: '0 7px',
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 800,
  background: 'var(--color-primary)',
  color: '#fff',
  boxShadow: '0 1px 3px var(--color-shadow)',
};

const ctrlBase = {
  minWidth: 36,
  minHeight: 36,
  width: 36,
  height: 36,
  padding: 0,
  borderRadius: 8,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
};

function ctrlBtnStyle(isDisabled) {
  if (isDisabled) {
    return {
      ...ctrlBase,
      border: '2px solid rgba(127, 127, 127, 0.22)',
      background: 'var(--color-bg)',
      color: 'var(--color-text)',
      opacity: 0.48,
      cursor: 'not-allowed',
    };
  }
  return {
    ...ctrlBase,
    border: '2px solid var(--color-border)',
    background: 'var(--color-bg)',
    color: 'var(--color-text)',
    cursor: 'pointer',
    opacity: 1,
  };
}

function coerceItem(entry, idx) {
  if (entry == null) return null;
  if (typeof entry === 'string') {
    const id = `s${idx}`;
    return { id, text: entry.trim() || id };
  }
  if (typeof entry === 'object') {
    const id = String(entry.id ?? entry.key ?? `s${idx}`).trim() || `s${idx}`;
    const text = String(entry.text ?? entry.label ?? entry.name ?? '').trim() || id;
    return { id, text, icon: entry.icon };
  }
  return null;
}

/**
 * Normalize legacy / alternate content shapes without requiring JSON rewrites.
 * @returns {{ items: {id:string,text:string,icon?:string}[], correctOrder: string[], goalFromContent: string, rawInstructions: string }}
 */
function normalizeSequenceContent(raw = {}) {
  const source = raw.items ?? raw.steps ?? raw.options;
  const arr = Array.isArray(source) ? source : [];
  const items = arr.map(coerceItem).filter(Boolean);
  let correctOrder = Array.isArray(raw.correctOrder) ? raw.correctOrder.map((id) => String(id)) : [];
  if (!correctOrder.length && Array.isArray(raw.answer)) {
    correctOrder = raw.answer.map((id) => String(id));
  }
  const goalFromContent = [raw.goal, raw.prompt].find((s) => typeof s === 'string' && s.trim()) || '';
  const rawInstructions = typeof raw.instructions === 'string' ? raw.instructions.trim() : '';
  return { items, correctOrder, goalFromContent: String(goalFromContent).trim(), rawInstructions };
}

/** Lowercase trimmed label for order checks; falls back to id so unknown ids still compare. */
function normalizeStepText(id, idToItem) {
  const raw = idToItem[id]?.text;
  if (typeof raw !== 'string' || !raw.trim()) return String(id).toLowerCase();
  return raw.replace(/\s+/g, ' ').trim().toLowerCase();
}

/**
 * True when each position's display text matches the goal order. Identical labels (e.g. two
 * "Forward" cards) are interchangeable; exact id order is a special case of this rule.
 */
function sequenceMatchesCorrectOrder(sequenceIds, correctOrder, idToItem) {
  if (correctOrder.length !== sequenceIds.length || !correctOrder.length) return false;
  return correctOrder.every(
    (correctId, i) =>
      normalizeStepText(sequenceIds[i], idToItem) === normalizeStepText(correctId, idToItem),
  );
}

const MECHANIC_INSTRUCTION_RX =
  /\b(tap|drag|dropp?|slide|swipe|arrow|pill|numbered|shuffle|put\s+the\s+cards|columns?|worksheet|up\s+and\s+down|re-?order|check\s+answer|in\s+the\s+right\s+order|into\s+the\s+best\s+order)\b/i;

function isMechanicInstruction(s) {
  return typeof s === 'string' && MECHANIC_INSTRUCTION_RX.test(s);
}

function inferSequenceHeadline(items, causeEffect) {
  if (causeEffect) return 'Build the chain';
  const blob = items.map((i) => `${i.text} ${i.icon || ''}`).join(' ').toLowerCase();
  if (
    /\b(forward|backward|backwards|left|right|repeat|loop|loops|stop|program|turn|move|roll|start|end)\b/.test(
      blob,
    ) ||
    /[{}]/.test(blob)
  ) {
    return 'Build the program';
  }
  return 'Put the steps in order';
}

function pickEngineHeadline(titleProp, guidedContext, items, causeEffect) {
  const stepTitle = typeof guidedContext?.stepTitle === 'string' ? guidedContext.stepTitle : '';
  const activityTitle = typeof titleProp === 'string' ? titleProp.trim() : '';

  // Step card already shows a title in guided context; skip the engine subtitle entirely.
  if (guidedContext?.singleSurface === true && stepTitle.trim()) {
    return null;
  }

  if (shouldSuppressActivityTitle({ stepTitle, activityTitle: titleProp })) return null;
  if (activityTitle) return activityTitle;
  return inferSequenceHeadline(items, causeEffect);
}

/**
 * Single prompt line inside the engine.
 * Tap-first guided shells used to duplicate this in `LessonPage` Goal callouts; sequencing now hides that
 * callout and renders one line here (`readableInstruction` overrides JSON goal/instructions overlap).
 */
function pickGoalLine(guidedContext, goalFromContent, rawInstructions, compactLesson) {
  const readable =
    typeof guidedContext?.readableInstruction === 'string' ? guidedContext.readableInstruction.trim() : '';
  const lead = typeof guidedContext?.stepInstructionLead === 'string' ? guidedContext.stepInstructionLead.trim() : '';
  const shellOneShot = compactLesson && guidedContext?.singleSurface === true;

  if (shellOneShot) {
    if (readable) return readable;
    if (lead) return lead;
    if (goalFromContent) return goalFromContent;
    if (rawInstructions && !isMechanicInstruction(rawInstructions)) return rawInstructions;
    return '';
  }

  if (lead) return lead;
  if (goalFromContent) return goalFromContent;
  if (rawInstructions && !isMechanicInstruction(rawInstructions)) return rawInstructions;
  return '';
}

function buildSequenceOneShot({ compactLesson, causeEffect, rawInstructions }) {
  let text;
  let audioText;
  if (compactLesson) {
    text = SEQUENCE_TAP_MECHANIC_HINT.text;
    audioText = SEQUENCE_TAP_MECHANIC_HINT.audioText;
  } else {
    text = causeEffect
      ? 'Order the story beats so each cause happens before its effect. Use the up and down arrows on each row to move a step, then tap Check answer.'
      : 'Use the up and down arrows on each row to reorder steps, then tap Check answer.';
    audioText = text;
  }
  if (rawInstructions && isMechanicInstruction(rawInstructions)) {
    text = `${text} (${rawInstructions})`;
    audioText = text;
  }
  return { text, audioText };
}

/**
 * Order steps correctly.
 * Content: {
 *   instructions?, items?, steps?, options?, correctOrder?, answer?, goal?, prompt?,
 *   items: [{ id, text, icon? }],
 *   correctOrder: string[],
 *   presentation?: 'default' | 'cause_effect',
 * }
 *
 * @param {'default'|'compact'|'tapOnly'|'mobileCard'} [lessonUiMode] — tap-first: Check moves to lesson footer via onRegisterLessonFooterActions
 */
export default function SequenceEngine({
  title = 'Put in order',
  content = {},
  hints = [],
  explanation,
  activityId = 'engine_sequence',
  onProgress,
  onComplete,
  lessonUiMode = 'default',
  onRegisterLessonFooterActions,
  guidedHintsEnabled = true,
  guidedContext,
}) {
  const { items, correctOrder, goalFromContent, rawInstructions } = useMemo(
    () => normalizeSequenceContent(content),
    [content],
  );
  const compactLesson = isTapFirstLessonUi(lessonUiMode);
  const causeEffect = content.presentation === 'cause_effect';

  const itemsSignature = useMemo(
    () => `${activityId}|${items.map((i) => i.id).join(',')}|${correctOrder.join(',')}`,
    [activityId, items, correctOrder],
  );

  const [order, setOrder] = useState(() => shuffleIds(items));
  const [tapBuildOrder, setTapBuildOrder] = useState([]);
  const [poolOrder, setPoolOrder] = useState(() => shuffleIds(items));
  const [checked, setChecked] = useState(null);
  const [finished, setFinished] = useState(false);
  const [showCelebration, celebrate] = useCelebration();
  const [a11yMsg, setA11yMsg] = useState('');

  const oneShotInteractionHint = useMemo(
    () => buildSequenceOneShot({ compactLesson, causeEffect, rawInstructions }),
    [compactLesson, causeEffect, rawInstructions],
  );

  const getExplanation = useCallback(() => content.explanation, [content.explanation]);

  const guided = useGuidedHints({
    activityId,
    hints,
    oneShotInteractionHint,
    explanation,
    getExplanation,
    isActivityComplete: finished,
    enabled: guidedHintsEnabled,
  });
  const { resetSession, noteUserAction, noteCorrectAnswer, noteIncorrectAnswer } = guided;

  useEffect(() => {
    resetSession();
    setOrder(shuffleIds(items));
    setPoolOrder(shuffleIds(items));
    setTapBuildOrder([]);
    setChecked(null);
    setFinished(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `items` matches itemsSignature; omit resetSession to avoid re-running when `finished` changes resetSession identity
  }, [itemsSignature]);

  const idToItem = useMemo(() => {
    const m = {};
    items.forEach((i) => {
      m[i.id] = i;
    });
    return m;
  }, [items]);

  const engineHeadline = useMemo(
    () => pickEngineHeadline(title, guidedContext, items, causeEffect),
    [title, guidedContext, items, causeEffect],
  );

  const goalLine = useMemo(
    () => pickGoalLine(guidedContext, goalFromContent, rawInstructions, compactLesson),
    [guidedContext, goalFromContent, rawInstructions, compactLesson],
  );

  const resetShuffle = useCallback(() => {
    resetSession();
    setOrder(shuffleIds(items));
    setPoolOrder(shuffleIds(items));
    setTapBuildOrder([]);
    setChecked(null);
    setFinished(false);
    onProgress?.({ activityId, engine: 'sequence', phase: 'reset' });
  }, [items, activityId, resetSession, onProgress]);

  const checkOrder = useCallback(() => {
    if (finished) return;
    noteUserAction();
    const sequenceIds = compactLesson ? tapBuildOrder : order;
    const ok = sequenceMatchesCorrectOrder(sequenceIds, correctOrder, idToItem);
    setChecked(ok);
    setA11yMsg(
      ok
        ? 'Nice! That order works.'
        : compactLesson
          ? 'Not quite. Try the order again.'
          : 'Not quite yet. Try moving a step, then check again.',
    );
    onProgress?.({ activityId, engine: 'sequence', phase: 'check', lastCorrect: ok });
    if (ok) {
      setFinished(true);
      celebrate();
      noteCorrectAnswer();
      trackMiniAppEvent.interaction(activityId, 'correct');
      onComplete?.({ activityId, engine: 'sequence', perfect: true });
      trackMiniAppEvent.completed(activityId);
    } else {
      noteIncorrectAnswer();
      trackMiniAppEvent.interaction(activityId, 'incorrect');
    }
  }, [
    finished,
    compactLesson,
    tapBuildOrder,
    correctOrder,
    idToItem,
    order,
    activityId,
    noteUserAction,
    noteCorrectAnswer,
    noteIncorrectAnswer,
    onProgress,
    onComplete,
    celebrate,
  ]);

  const revealAnswer = useCallback(() => {
    if (finished) return;
    noteUserAction();
    if (compactLesson) {
      setTapBuildOrder([...correctOrder]);
    } else {
      setOrder([...correctOrder]);
    }
    setChecked(true);
    setFinished(true);
    celebrate();
    noteCorrectAnswer();
    trackMiniAppEvent.interaction(activityId, 'reveal_answer');
    onProgress?.({ activityId, engine: 'sequence', phase: 'reveal_answer' });
    onComplete?.({ activityId, engine: 'sequence', perfect: false, revealed: true });
    trackMiniAppEvent.completed(activityId);
  }, [
    finished,
    compactLesson,
    correctOrder,
    activityId,
    noteUserAction,
    noteCorrectAnswer,
    celebrate,
    onProgress,
    onComplete,
  ]);

  const moveUp = useCallback(
    (idx) => {
      if (finished || compactLesson) return;
      if (idx <= 0) return;
      noteUserAction();
      setOrder((o) => {
        const next = [...o];
        [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
        return next;
      });
      setChecked(null);
      onProgress?.({ activityId, engine: 'sequence', phase: 'reorder', index: idx });
      trackMiniAppEvent.interaction(activityId, 'reorder');
    },
    [finished, compactLesson, activityId, noteUserAction, onProgress],
  );

  const moveDown = useCallback(
    (idx) => {
      if (finished || compactLesson) return;
      if (idx >= order.length - 1) return;
      noteUserAction();
      setOrder((o) => {
        const next = [...o];
        [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
        return next;
      });
      setChecked(null);
      onProgress?.({ activityId, engine: 'sequence', phase: 'reorder', index: idx });
      trackMiniAppEvent.interaction(activityId, 'reorder');
    },
    [finished, compactLesson, order.length, activityId, noteUserAction, onProgress],
  );

  const checkAnswerDisabled =
    compactLesson && !finished && tapBuildOrder.length !== correctOrder.length;

  useEffect(() => {
    if (!onRegisterLessonFooterActions) return undefined;
    if (finished) {
      onRegisterLessonFooterActions(null);
      return undefined;
    }
    onRegisterLessonFooterActions({
      checkAnswer: () => checkOrder(),
      showAnswer: revealAnswer,
      checkAnswerDisabled,
    });
    return () => onRegisterLessonFooterActions(null);
  }, [onRegisterLessonFooterActions, finished, checkOrder, revealAnswer, checkAnswerDisabled]);

  if (items.length === 0 || correctOrder.length === 0) {
    return (
      <div style={missionShell}>
        <p style={instructionsStyle}>No sequencing steps in this activity yet.</p>
      </div>
    );
  }

  const titleHeadStyle = {
    margin: '0 0 6px',
    fontSize: 'clamp(1.02rem, 3.2vw, 1.22rem)',
    fontWeight: 800,
    color: 'var(--color-text)',
    lineHeight: 1.22,
  };

  const goalBodyStyle = {
    margin: '0 0 10px',
    lineHeight: 1.45,
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--color-text)',
    opacity: 0.9,
  };

  const listWrapStyle = finished
    ? {
        padding: 8,
        borderRadius: 16,
        border: '1px solid rgba(34, 197, 94, 0.45)',
        background: 'rgba(34, 197, 94, 0.06)',
      }
    : { padding: 0 };

  const rowList = compactLesson && finished ? correctOrder : order;

  const tapShellPad =
    compactLesson && !finished
      ? { paddingBottom: 'max(12px, env(safe-area-inset-bottom, 0px))' }
      : {};

  const chipBtnStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    minHeight: 44,
    maxWidth: '100%',
    borderRadius: 999,
    border: '2px solid var(--color-primary)',
    background: 'rgba(139, 92, 246, 0.12)',
    color: 'var(--color-text)',
    fontWeight: 700,
    fontSize: 13,
    lineHeight: 1.25,
    cursor: 'pointer',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  };

  const chipBadgeStyle = {
    ...badgeStyle,
    minWidth: 22,
    height: 22,
    padding: '0 6px',
    fontSize: 11,
  };

  return (
    <div
      style={{
        ...missionShell,
        position: 'relative',
        ...(guided.contentReserveStyle || {}),
        ...tapShellPad,
      }}
    >
      <style>
        {`
          .seq-mission-row-success {
            border-color: rgba(34, 197, 94, 0.5) !important;
            background: rgba(34, 197, 94, 0.09) !important;
          }
          @keyframes seqChainPop {
            from { transform: scale(0.94); opacity: 0.35; }
            to { transform: scale(1); opacity: 1; }
          }
          .seq-cause-row-anim {
            animation: seqChainPop 0.42s ease-out backwards;
          }
        `}
      </style>
      <CelebrationOverlay visible={showCelebration} message="Nice! That order works!" />
      <A11yAnnouncement message={a11yMsg} />
      <GuidedHintOverlay
        open={guided.open}
        isNarrow={guided.isNarrow}
        visibleLevel={guided.visibleLevel}
        maxLevel={guided.maxLevel}
        isInteractionPhase={guided.isInteractionPhase}
        anotherClueEnabled={guided.anotherClueEnabled}
        hintEntry={guided.hintEntry}
        showHowAvailable={guided.showHowAvailable}
        resolvedExplanation={guided.resolvedExplanation}
        explanationOpen={guided.explanationOpen}
        onDismiss={guided.dismissOverlay}
        onAnotherClue={guided.anotherClue}
        onReadAloud={() =>
          guidedHintReadAloud(guided.hintEntry, activityId, guided.readAloudTrackLevel)
        }
        onShowHow={guided.openExplanation}
      />
      <GuidedHintHelpButton visible={guided.showHelpButton} onClick={guided.requestHintManually} />

      {engineHeadline ? (
        <h2 style={titleHeadStyle}>{engineHeadline}</h2>
      ) : null}
      {goalLine ? (
        <p style={{ ...goalBodyStyle, margin: compactLesson ? '0 0 6px' : '0 0 10px' }}>{goalLine}</p>
      ) : null}

      <div style={listWrapStyle}>
        {compactLesson && !finished ? (
          <>
            <p
              style={{
                margin: '0 0 6px',
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: 'var(--color-text-soft, var(--color-text))',
                opacity: 0.85,
              }}
            >
              Choices
            </p>
            <div
              role="group"
              aria-label="Steps to add"
              style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}
            >
              {poolOrder
                .filter((id) => !tapBuildOrder.includes(id))
                .map((itemId) => {
                  const icon = idToItem[itemId]?.icon;
                  return (
                    <button
                      type="button"
                      key={`pool-${itemId}`}
                      style={{
                        ...tapRoundNeutralChoiceBtn,
                        width: '100%',
                        minHeight: 48,
                        borderRadius: 12,
                        fontSize: 'clamp(14px, 3.4vw, 16px)',
                        padding: '10px 12px',
                      }}
                      onClick={() => {
                        noteUserAction();
                        setTapBuildOrder((t) => [...t, itemId]);
                        setChecked(null);
                        trackMiniAppEvent.interaction(activityId, 'tap_append');
                      }}
                    >
                      {icon ? (
                        <span style={{ marginRight: 8, fontSize: 20 }} aria-hidden>
                          {icon}
                        </span>
                      ) : null}
                      {idToItem[itemId]?.text || itemId}
                    </button>
                  );
                })}
            </div>

            {tapBuildOrder.length === 0 ? (
              <p
                style={{
                  margin: '0 0 4px',
                  fontSize: 12,
                  fontWeight: 600,
                  lineHeight: 1.35,
                  color: 'var(--color-text-soft, var(--color-text))',
                  opacity: 0.72,
                }}
              >
                Your order will appear here.
              </p>
            ) : (
              <div style={{ marginTop: 2, marginBottom: 8 }}>
                <p
                  style={{
                    margin: '0 0 4px',
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    color: 'var(--color-text-soft, var(--color-text))',
                    opacity: 0.85,
                  }}
                >
                  Your order
                  {tapBuildOrder.length < correctOrder.length ? (
                    <span style={{ fontWeight: 700, textTransform: 'none', marginLeft: 6, opacity: 0.9 }}>
                      ({tapBuildOrder.length} of {correctOrder.length})
                    </span>
                  ) : null}
                </p>
                <div
                  role="list"
                  aria-label="Built sequence"
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 6,
                    alignItems: 'center',
                  }}
                >
                  {tapBuildOrder.map((itemId, idx) => {
                    const icon = idToItem[itemId]?.icon;
                    return (
                      <button
                        type="button"
                        key={`chip-${itemId}-${idx}`}
                        role="listitem"
                        onClick={() => {
                          noteUserAction();
                          setTapBuildOrder((t) => t.slice(0, idx));
                          setChecked(null);
                          onProgress?.({ activityId, engine: 'sequence', phase: 'tap_truncate', index: idx });
                        }}
                        style={chipBtnStyle}
                        aria-label={`Step ${idx + 1}: ${idToItem[itemId]?.text || itemId}. Tap to remove this step and everything after.`}
                      >
                        <span style={chipBadgeStyle} aria-hidden>
                          {idx + 1}
                        </span>
                        {icon ? (
                          <span style={{ fontSize: 18, lineHeight: 1 }} aria-hidden>
                            {icon}
                          </span>
                        ) : null}
                        <span style={{ textAlign: 'left', wordBreak: 'break-word' }}>
                          {idToItem[itemId]?.text || itemId}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {tapBuildOrder.length ? (
              <button
                type="button"
                style={{ ...secondaryBtn, marginTop: 4, width: '100%', minHeight: 44 }}
                onClick={() => {
                  noteUserAction();
                  setTapBuildOrder([]);
                  setChecked(null);
                }}
              >
                Clear sequence
              </button>
            ) : null}
          </>
        ) : (
          <div
            role="list"
            aria-label={
              compactLesson
                ? 'Steps in the correct order.'
                : 'Steps to put in order. Use the up and down arrow buttons on each row.'
            }
            style={{ margin: 0, padding: 0, listStyle: 'none', overflowY: 'auto' }}
          >
            {rowList.map((itemId, idx) => {
              const rowClass = finished ? 'seq-mission-row-success' : '';
              const upDisabled = finished || compactLesson || idx === 0;
              const downDisabled = finished || compactLesson || idx === rowList.length - 1;

              const icon = idToItem[itemId]?.icon;
              const animClass = finished && causeEffect ? 'seq-cause-row-anim' : '';
              return (
                <div
                  key={`${itemId}-${idx}`}
                  role="listitem"
                  className={[rowClass, animClass].filter(Boolean).join(' ')}
                  style={{
                    marginBottom: idx === rowList.length - 1 ? 0 : 6,
                    padding: '8px 12px',
                    background: 'var(--color-bg)',
                    borderRadius: 12,
                    border: '1px solid var(--color-border)',
                    boxShadow: finished ? '0 1px 6px rgba(34, 197, 94, 0.1)' : '0 1px 4px var(--color-shadow)',
                    outline: 'none',
                    animationDelay: finished && causeEffect ? `${idx * 0.12}s` : undefined,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                    }}
                  >
                    <span style={{ ...badgeStyle, flexShrink: 0 }} aria-hidden="true">
                      {idx + 1}
                    </span>
                    <span
                      style={{
                        flex: 1,
                        fontSize: 14,
                        fontWeight: 650,
                        color: 'var(--color-text)',
                        lineHeight: 1.32,
                        minWidth: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      {icon ? (
                        <span style={{ fontSize: 20, lineHeight: 1, flexShrink: 0 }} aria-hidden>
                          {icon}
                        </span>
                      ) : null}
                      <span>{idToItem[itemId]?.text || itemId}</span>
                    </span>
                    {!finished && !compactLesson ? (
                      <div
                        style={{
                          display: 'flex',
                          gap: 6,
                          flexShrink: 0,
                        }}
                      >
                        <button
                          type="button"
                          aria-label={`Move step ${idx + 1} up`}
                          disabled={upDisabled}
                          style={ctrlBtnStyle(upDisabled)}
                          onClick={() => moveUp(idx)}
                        >
                          <ChevronUp size={18} strokeWidth={2.5} aria-hidden />
                        </button>
                        <button
                          type="button"
                          aria-label={`Move step ${idx + 1} down`}
                          disabled={downDisabled}
                          style={ctrlBtnStyle(downDisabled)}
                          onClick={() => moveDown(idx)}
                        >
                          <ChevronDown size={18} strokeWidth={2.5} aria-hidden />
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {!compactLesson && !onRegisterLessonFooterActions ? (
        <button
          type="button"
          style={{ ...primaryBtn, marginTop: 12 }}
          onClick={checkOrder}
          disabled={finished}
        >
          Check answer
        </button>
      ) : null}

      {!compactLesson && !onRegisterLessonFooterActions ? (
        <button
          type="button"
          style={{ ...secondaryBtn, marginTop: 10, minHeight: 44, fontSize: 13 }}
          onClick={resetShuffle}
          disabled={finished}
          aria-label="Shuffle steps into a new random order"
        >
          Shuffle order
        </button>
      ) : null}

      {checked === false && (
        <div style={{ ...feedbackWarmHint, marginTop: 10 }} role="status">
          {compactLesson
            ? 'Not quite. Try the order again.'
            : 'Almost — use the arrows to reorder the steps, then tap Check answer again.'}
        </div>
      )}
      {finished && (
        <div style={{ ...feedbackCorrect, marginTop: 10 }} role="status">
          ✓ Nice! That order works!
        </div>
      )}
    </div>
  );
}
